import React, { useEffect, useRef, useState } from 'react';
import { ReactSketchCanvas, ReactSketchCanvasRef } from 'react-sketch-canvas';
import { Loader2Icon, Trash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getUserCertificates } from '@/use-cases/get-user-signatures';
import { getSignatureSrokeDownloadUrl } from '@/use-cases/get-certificate-stroke-download-url';
import { getSignatureStrokeUploadUrl } from '@/use-cases/get-signature-stroke-upload-url';
import { rawHttp } from '@/http';
import { Certificate } from '@/entities/certificate';
import { useSidebar } from '@/provider/sidebar/sidebar-context';
import notifyHB from '@/config/notifyHB';
import { toast } from '@/components/toast/toast';
import styles from './my-manuscript.module.scss';

function dataURItoBlob(dataURI: string) {
	const binary = atob(dataURI.split(',')[1]);
	const array = [];
	for (let i = 0; i < binary.length; i++) {
		array.push(binary.charCodeAt(i));
	}
	return new Blob([new Uint8Array(array)], { type: 'image/png' });
}

// The sketch is vector (SVG) internally; export it at a fixed high resolution so
// the stored PNG stays crisp when the Signer scales it into the signature field.
const MANUSCRIPT_EXPORT_WIDTH = 1600;

export const MyManuscript: React.FC = () => {
	const { hideSidebar } = useSidebar();
	const [activeCertificate, setActiveCertificate] =
		useState<Certificate | null>(null);
	const [strokeUrl, setStrokeUrl] = useState<string | null>(null);
	const [isFetchingStrokeUrl, setIsFetchingStrokeUrl] = useState(false);
	const [isDrawing, setIsDrawing] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const canvasRef = useRef<ReactSketchCanvasRef>(null);
	const drawingAreaRef = useRef<HTMLDivElement>(null);

	const fetchManuscript = (certSerial: string) => {
		setIsFetchingStrokeUrl(true);
		getSignatureSrokeDownloadUrl(certSerial)
			.then((url) => {
				if (url) setStrokeUrl(url);
			})
			.catch(console.debug)
			.finally(() => setIsFetchingStrokeUrl(false));
	};

	useEffect(() => {
		getUserCertificates()
			.then((certs) => {
				const first = certs?.find((c) => c.status === 'Active') ?? null;
				setActiveCertificate(first);
				if (first) fetchManuscript(first.certificate_id);
			})
			.catch((error) => {
				notifyHB('my-manuscript', 'MyManuscript', error);
			});
	}, []);

	const saveManuscript = async () => {
		if (!activeCertificate) return;
		setIsSaving(true);
		try {
			// Export at MANUSCRIPT_EXPORT_WIDTH, keeping the on-screen aspect
			// ratio so the drawing is not distorted.
			const area = drawingAreaRef.current?.getBoundingClientRect();
			const aspectRatio =
				area && area.width > 0 ? area.height / area.width : 0.5;
			const dataUri = await canvasRef.current?.exportImage('png', {
				width: MANUSCRIPT_EXPORT_WIDTH,
				height: Math.round(MANUSCRIPT_EXPORT_WIDTH * aspectRatio),
			});
			if (!dataUri) return;

			const blob = dataURItoBlob(dataUri);
			const file = new File(
				[blob],
				`${Date.now()}-${activeCertificate.certificate_id}`,
				{ type: blob.type, lastModified: Date.now() },
			);

			const uploadUrlData = await getSignatureStrokeUploadUrl(
				activeCertificate.certificate_id,
			);
			const formData = new FormData();
			formData.append('key', uploadUrlData.fields.key);
			formData.append('x-amz-algorithm', uploadUrlData.fields['x-amz-algorithm']);
			formData.append('x-amz-credential', uploadUrlData.fields['x-amz-credential']);
			formData.append('x-amz-date', uploadUrlData.fields['x-amz-date']);
			formData.append('x-amz-security-token', uploadUrlData.fields['x-amz-security-token']);
			formData.append('policy', uploadUrlData.fields.policy);
			formData.append('x-amz-signature', uploadUrlData.fields['x-amz-signature']);
			formData.append('file', file, file.name);

			await rawHttp.post(uploadUrlData.uploadUrl, formData);

			setIsDrawing(false);
			fetchManuscript(activeCertificate.certificate_id);
			toast({ mode: 'default', description: 'Rúbrica guardada' });
		} catch (error) {
			console.debug(error);
			toast({ mode: 'destructive', description: 'No fue posible guardar la Rúbrica. Intenta de nuevo.' });
			notifyHB('my-manuscript', 'MyManuscript', error);
		} finally {
			setIsSaving(false);
		}
	};

	const hasNoCertificate = !isFetchingStrokeUrl && !activeCertificate;

	return (
		<div className={styles['my-manuscript']}>
			<div className={`cyber-backdrop ${styles['backdrop']}`} />
			<div className="cyber-main with-sidenav" onClick={hideSidebar}>
				<div className="cyber-spacer section" />
				<div className={styles['container']}>
					<div className={styles['body']}>
						<div className={styles['header']}>
							<h1>Mi Rúbrica</h1>
						</div>
						<p className={styles['description']}>
							Esta Rúbrica se aplicará a todos tus certificados al firmar un documento.
						</p>

						{hasNoCertificate ? (
							<p className={styles['no-certificate']}>
								Necesitas al menos un certificado activo para gestionar tu Rúbrica.
							</p>
						) : (
							<div className={styles['manuscript-card']}>
								<div
									ref={drawingAreaRef}
									className={styles['manuscript-area']}
								>
									{isDrawing ? (
										<ReactSketchCanvas
											ref={canvasRef}
											strokeWidth={2}
											strokeColor="black"
											canvasColor="transparent"
											style={{ border: 'none' }}
											width="100%"
											height="100%"
										/>
									) : isFetchingStrokeUrl ? (
										<div className={styles['loading']}>
											<Loader2Icon className="animate-spin" />
											<span>Cargando Rúbrica</span>
										</div>
									) : strokeUrl ? (
										<img
											className={styles['manuscript-preview']}
											src={strokeUrl}
											alt="Rúbrica guardada"
										/>
									) : (
										<div className={styles['no-manuscript']}>
											<img src="/logo.png" alt="" />
											<span>No hay Rúbrica</span>
										</div>
									)}
								</div>

								{isDrawing ? (
									<div className={styles['drawing-actions']}>
										<Button
											variant="ghost"
											onClick={() => setIsDrawing(false)}
											disabled={isSaving}
										>
											Cancelar
										</Button>
										<Button
											variant="ghost"
											onClick={() => canvasRef.current?.clearCanvas()}
											disabled={isSaving}
										>
											<Trash color="#bd404f" />
										</Button>
										<Button
											variant="default"
											onClick={saveManuscript}
											disabled={isSaving}
										>
											{isSaving && <Loader2Icon className="animate-spin" />}
											Guardar
										</Button>
									</div>
								) : (
									<Button
										variant="ghost"
										onClick={() => setIsDrawing(true)}
										disabled={!activeCertificate || isFetchingStrokeUrl}
									>
										Agregar/modificar
									</Button>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
